import api from './api';
import { API_ENDPOINTS } from '../constants';

export interface AdminUserRole {
  id: number;
  name: string;
}

export interface AdminUser {
  id: number;
  userName: string;
  role: AdminUserRole;
}

export interface UsersApiResponse {
  items: AdminUser[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface UsersListResponse {
  data: AdminUser[];
  current_page: number;
  last_page: number;
  per_page: number;
  total?: number;
}

export interface UserCreateData {
  userName: string;
  password: string;
  roleId: number;
}

export interface UserUpdateData {
  userName?: string;
  password?: string;
  roleId?: number;
}

export interface Role {
  id: number;
  name: string;
}

export const userService = {
  /** GET /api/v1/admin/users — список пользователей (page, limit) */
  getUsersList: async (page: number = 1, limit: number = 10): Promise<UsersListResponse> => {
    const { data } = await api.get<UsersApiResponse>(API_ENDPOINTS.USERS_LIST, {
      params: { page, limit },
    });
    return {
      data: Array.isArray(data.items) ? data.items : [],
      current_page: data.page,
      last_page: data.totalPages,
      per_page: data.pageSize,
      total: data.totalCount,
    };
  },

  /** GET /api/v1/admin/users/{id} */
  getUser: async (id: number): Promise<AdminUser> => {
    const { data } = await api.get<AdminUser>(API_ENDPOINTS.USER_GET(id));
    return data;
  },

  /** GET /api/v1/admin/roles */
  getRoles: async (): Promise<Role[]> => {
    const { data } = await api.get<Role[]>(API_ENDPOINTS.ROLES_LIST);
    return Array.isArray(data) ? data : [];
  },

  /** POST /api/v1/admin/users */
  createUser: async (data: UserCreateData): Promise<AdminUser> => {
    const { data: result } = await api.post<AdminUser>(API_ENDPOINTS.USERS_LIST, data);
    return result;
  },

  /** PATCH /api/v1/admin/users/{id} */
  updateUser: async (id: number, data: UserUpdateData): Promise<AdminUser> => {
    const body: Record<string, unknown> = {};
    if (data.userName !== undefined) body.userName = data.userName;
    if (data.password !== undefined) body.password = data.password;
    if (data.roleId !== undefined) body.roleId = data.roleId;
    const { data: result } = await api.patch<AdminUser>(API_ENDPOINTS.USER_GET(id), body);
    return result;
  },

  /** DELETE /api/v1/admin/users/{id} */
  deleteUser: async (id: number): Promise<void> => {
    await api.delete(API_ENDPOINTS.USER_GET(id));
  },
};
